import bindAllOfThis from 'src/utils/BindThisHelper';
import { TiledGameObject } from 'src/games/shared/phaserHelpers/HandyTypes';
import { RocketPart } from '../objects/RocketPart';
import { GameState } from 'src/games/RocketRepairGame/objects/GameState';
import { ToolBin, Box, BoxConfig } from '../objects/ToolBin';
import { Color } from 'src/games/shared/ColorValue';
import TranslateApi from 'src/utils/TranslateHelper';
import initializeInteractiveCursor from '../../shared/InteractiveCursorUtil';
import { PhaserModalConfig, PhaserModal } from 'src/games/shared/Modal';
import { EndGameScene } from 'src/games/RocketRepairGame/scenes/EndGameScene';
import { PhaserButtonConfig } from 'src/games/shared/Button';
import AudioTranslations from '../../../constants/AudioTranslations';

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    part: RocketPart;
    bin: ToolBin;
    button: Phaser.GameObjects.Sprite;
    boxes: Box[];
    conveyorBelt: Phaser.GameObjects.Sprite;
    ship: Phaser.GameObjects.Sprite;
    partOb: TiledGameObject;
    binOb: TiledGameObject;


    beltDistance: number = 1200;

    private debug: boolean = false;

    id: number = 1;
    assemblyAssets = [
        require("src/assets/qci/sprites/gameRocketRepair/assembly1.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly2.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly3.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly4.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly5.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly6.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly7.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly8.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly9.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly10.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly11.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly12.png"),
        require("src/assets/qci/sprites/gameRocketRepair/assembly13.png"),
    ];

    constructor() {
        super({
            key: "MainScene" + Math.random()
        });
        bindAllOfThis(this, MainScene.prototype);
    }

    preload(): void {
        this.load.image('background', require("/src/assets/qci/backgrounds/2r_background.png"));
        this.load.tilemapTiledJSON("map", require("/src/assets/qci/maps/rocket_repair.json"));
        this.load.spritesheet("button", require("src/assets/qci/sprites/gameRocketRepair/button.png"), { frameWidth: 275, frameHeight: 110 });
        this.load.image('box', require("src/assets/qci/sprites/gameRocketRepair/parts_box.png"));
        this.load.spritesheet("belt", require("src/assets/qci/sprites/gameRocketRepair/conveyor.png"), { frameWidth: 1360, frameHeight: 115 });
        this.load.spritesheet("bin", require("src/assets/qci/sprites/gameRocketRepair/bin.png"), { frameWidth: 400, frameHeight: 150 });
        this.load.spritesheet("monitor", require("src/assets/qci/sprites/gameRocketRepair/monitor.png"), { frameWidth: 118, frameHeight: 95 });
        this.load.spritesheet("items", require("src/assets/qci/sprites/gameRocketRepair/parts.png"), { frameWidth: 80, frameHeight: 95 });

        // Component and Assembly spritesheets
        var i = 1;
        for (var assembly of this.assemblyAssets) {
            this.load.spritesheet("assembly" + i, assembly, { frameWidth: 990, frameHeight: 550 });
            i++;
        }

        for (var part of RocketPart.ROCKET_PARTS) {
            this.load.spritesheet(part.name, part.src, { frameWidth: part.frameWidth, frameHeight: part.frameHeight });
        }

        this.loadAudio();        
    }

    loadAudio(): void {
        let soundbits: string[] = ["tooLittleScrews", "tooManyScrews", "tooLittleBolts", "tooManyBolts", "correct"];
        soundbits.forEach(soundKey => {
            let key: string = "RocketRepair.Phaser." + soundKey;
            this.load.audio(key, AudioTranslations.FindByKeyAndLanguage(key, TranslateApi.currentLanguage.code).url);
        });
    }

    initialize(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: "map" });
        this.boxes = [];
    }

    create(): void {
        this.initialize();
        var background = this.add.image(0, 0, "background");
        background.setOrigin(0);
        background.setDisplaySize(this.gameWidth, this.gameHeight);

        this.addObjects();
        this.registerEvents();
        initializeInteractiveCursor(this);

        // Start game by moving everything along the conveyor belt
        this.movePartIn();
    }

    registerEvents(): void {
        this.input.dragDistanceThreshold = 10;
        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, box: Box) => {
            this.children.bringToTop(box);
        });

        if (this.debug) {
            this.input.on('dragenter', (pointer: Phaser.Input.Pointer, box: Box, dropzone) => {
                dropzone.setTint(Color.COLORS.get("blue"));
            });

            this.input.on('dragleave', (pointer: Phaser.Input.Pointer, box: Box, dropzone) => {
                dropzone.setTint(Color.COLORS.get("light grey"));
            });
        }

        this.input.on('drag', (pointer: Phaser.Input.Pointer, box: Box, dragX: number, dragY: number) => {
            box.x = dragX;
            box.y = dragY;
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, box: Box, dropped: boolean) => {
            if (!dropped) {
                this.putBack(box);
            }
        });

        this.input.on('drop', (pointer: Phaser.Input.Pointer, box: Box, toolBin: ToolBin) => {
            // If it's in the toolBin, add it to the items
            this.addBoxToToolBin(box);
        });
    }

    addObjects(): void {
        this.map.getObjectLayer('Objects').objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "belt") {
                this.conveyorBelt = this.add.sprite(0, ob.y, "belt", 0);
                this.conveyorBelt.setOrigin(0);

                this.anims.create({
                    key: "moveBelt",
                    frames: this.anims.generateFrameNumbers("belt", { start: 0, end: 4 }),
                    frameRate: 14,
                    repeat: 8
                });
            }
            else if (ob.name === "part") {
                this.partOb = ob;
                this.createPart();
            }
            else if (ob.name === "bin") {
                this.binOb = ob;
                this.createToolBin();
            }
            else if (ob.name === "button") {
                this.button = this.add.sprite(ob.x, ob.y, "button", 1);
                this.button.setOrigin(0);
                this.button.setInteractive({ useHandCursor: true });

                this.button.on('pointerover', () => {
                    this.button.setFrame(0);
                });

                this.button.on('pointerout', () => {
                    this.button.setFrame(1);
                });

                this.button.on('pointerdown', () => {
                    if (this.tweens.isTweening(this.part)) {
                        return;
                    }
                    this.button.disableInteractive();
                    this.checkCount();
                });
            }
            else if (ob.name === "parts_box") {
                var partsBox = this.add.image(ob.x, ob.y, "box");
                partsBox.setOrigin(0);
            }
            else if (ob.name === "assembly") {
                this.ship = this.add.sprite(ob.x, ob.y, GameState.current.shipTexture, 0);
                this.ship.setOrigin(0);
                this.anims.create({
                    key: "flashy_" + GameState.current.shipTexture,
                    frames: this.anims.generateFrameNumbers(GameState.current.shipTexture, { start: 0, end: 1 }),
                    frameRate: 2,
                    repeat: -1,
                });
                this.ship.anims.play('flashy_' + GameState.current.shipTexture);
            }
            else if (ob.name === "monitor") {
                var monitor = this.add.sprite(ob.x, ob.y, "monitor", 0);
                monitor.setOrigin(0);
                this.anims.create({
                    key: "monitorCursor",
                    frames: this.anims.generateFrameNumbers("monitor", { start: 0, end: 1 }),
                    frameRate: 2,
                    repeat: -1
                });
                monitor.anims.play("monitorCursor");
            }
        });

        this.createBoxes();
    }

    createBoxes() {
        this.map.getObjectLayer("Items").objects.forEach((ob: TiledGameObject) => {
            var config = {
                frame: ob.properties["frame"] as number,
                count: ob.properties["count"] as number,
                type: ob.properties["type"] as "Screw" | "Bolt",
            } as BoxConfig;

            // Placeholder - static image of the box
            var placeholder = this.add.image(ob.x + ob.width / 2, ob.y + ob.height / 2, "items", config.frame);

            // Only one actual draggable box
            var box = new Box(this, ob.x + ob.width / 2, ob.y + ob.height / 2, config, this.id++);
            this.boxes.push(box);
        });
    }

    checkCount(): void {
        var correctScrewCount = this.part.screws;
        var correctBoltCount = this.part.bolts;

        if (this.bin.screwCount < correctScrewCount) {
            this.modal("tooLittleScrews", "tryAgain");
        }
        else if (this.bin.screwCount > correctScrewCount) {
            this.modal("tooManyScrews", "tryAgain");
        }
        else if (this.bin.boltCount < correctBoltCount) {
            this.modal("tooLittleBolts", "tryAgain");
        }
        else if (this.bin.boltCount > correctBoltCount) {
            this.modal("tooManyBolts", "tryAgain");
        }
        else {
            // Move on to next part
            this.modal("correct", "next", true);
        }
    }

    putBack(box: Box): void {
        if (box.inToolBin) {
            this.removeBoxFromToolBin(box);
        }

        this.tweens.add({
            targets: box,
            x: box.pilePosition.x,
            y: box.pilePosition.y,
            duration: 800,
            ease: Phaser.Math.Easing.Back.Out,
        });
    }

    nextPart(): void {
        // Change current game state
        var gameOver = GameState.getNextPart();
        this.updateShip();

        if (gameOver) {
            setTimeout(() => {
                this.input.setDefaultCursor("default");
                this.input.removeAllListeners();
                this.scene.add("EndGameScene", EndGameScene, true);
                this.scene.remove(this);
            }, 2000);
        }
        else {
            this.createPart();
            this.createToolBin();
            this.movePartIn();
        }
    }

    updateShip() {
        this.ship.anims.stop();
        this.ship.setTexture(GameState.current.shipTexture, 0);
        this.anims.create({
            key: "flashy_" + GameState.current.shipTexture,
            frames: this.anims.generateFrameNumbers(GameState.current.shipTexture, { start: 0, end: 1 }),
            frameRate: 2,
            repeat: -1,
        });
        this.ship.anims.play('flashy_' + GameState.current.shipTexture);
    }

    /**
     * Creates a tool bin as a custom dropzone
     */
    createToolBin(): void {
        var ob = this.binOb;
        this.bin = new ToolBin(this, ob, "bin");
        this.bin.x -= this.beltDistance;
        this.bin.actualX = ob.x + ob.width / 2;
    }

    createPart(): void {
        var partConfig = GameState.current.partConfig;
        var x = this.binOb.x - partConfig.frameWidth - 50;
        var y = this.bin.y - partConfig.frameHeight / 2;

        // Big wings - adjusted position to keep from covering boxes
        if (partConfig.id === 12) {
            x = this.binOb.x - partConfig.frameHeight - 50;
            y = this.binOb.y + partConfig.frameWidth / 2;
        }

        this.part = new RocketPart(this, x - this.beltDistance, y, GameState.current.partConfig);
    }

    removeBoxFromToolBin(box: Box): void {
        box.inToolBin = false;
        box.type === "Screw" ? this.bin.screwCount -= box.count : this.bin.boltCount -= box.count;
        var boxToRemove = this.bin.boxes.findIndex((b: Box) => b.id === box.id);
        this.bin.boxes.splice(boxToRemove, 1);
    }

    addBoxToToolBin(box: Box): void {
        if (box.inToolBin) {
            return;
        }

        // Create a new box inside the bin
        var newBox = new Box(this, box.x, this.bin.y, box.config, this.id++);
        newBox.pilePosition = box.pilePosition;
        this.boxes.push(newBox);

        // Put back the actual box
        box.x = box.pilePosition.x;
        box.y = box.pilePosition.y;

        newBox.inToolBin = true;
        newBox.type === "Screw" ? this.bin.screwCount += newBox.count : this.bin.boltCount += newBox.count;
        this.bin.boxes.push(newBox);
    }

    modal(textKey: string, buttonKey: string, moveOn?: boolean) {
        let key = "RocketRepair.Phaser." + textKey;
        var text = TranslateApi.translate(key);
        let sound = this.sound.add(key);
        if (sound.duration) {
            sound.play();
        }

        var buttonConfig: PhaserButtonConfig = {
            smartSize: true,
            backgroundColor: Color.KUDER_PRIMARY_BUTTON
        };

        var config: PhaserModalConfig = {
            title: text,
            buttonWidth: 200,
            buttonConfig: buttonConfig
        };
        var buttonText = TranslateApi.translate("RocketRepair.Phaser." + buttonKey);

        var options = [{ id: 1, displayText: buttonText }];
        var modal = new PhaserModal(this, 0, 0, options, config);
        modal.setDepth(10);
        modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2);
        modal.on("optionClicked", () => {
            modal.destroy();
            this.button.setInteractive();
            if (moveOn) {
                // They got it right, so go to the next part
                this.movePartOut();
            }
        });
    }

    /**
     *  Move everything on conveyor belt to the right
     */
    movePartOut(): void {
        this.disableEverything();
        var targets = [this.part, this.bin];
        this.bin.boxes.forEach((i) => targets.push(i));
        this.conveyorBelt.anims.play("moveBelt");
        this.tweens.add({
            targets: targets,
            x: `+=${this.beltDistance}`,
            ease: "Linear",
            duration: 3000,
            onComplete: () => {
                this.part.destroy();
                this.bin.destroy();
                this.nextPart();
            }
        });
    }

    /**
     * Move everything on conveyor belt from the left
     */
    movePartIn(): void {
        var targets = [this.part, this.bin];
        this.disableEverything();
        this.conveyorBelt.anims.play("moveBelt");

        this.tweens.add({
            targets: targets,
            x: `+=${this.beltDistance}`,
            ease: "Linear",
            duration: 3000,
            onComplete: () => {
                this.enableEverything();
            }
        });
    }

    enableEverything(): void {
        this.button.setInteractive();
        this.boxes.forEach((box) => {
            box.input.enabled = true;
        });
    }

    disableEverything() {
        this.button.disableInteractive();
        this.boxes.forEach((box) => {
            box.input.enabled  = false;
        });
    }
}



// WEBPACK FOOTER //
// ./src/games/RocketRepairGame/scenes/MainScene.ts